import { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import api from '../../API';
import SetupPageLayout from '../setup/SetupPageLayout';
import WizardFooter from '../wizard/WizardFooter';
import FilenamePatternField from '../filename/FilenamePatternField';
import styles from './StainDefinitionWizard.module.css';

function StainDefinitionWizard({ initialItem = null, onComplete, onCancel }) {
    const queryClient = useQueryClient();
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const isEditMode = !!initialItem;
    
    // Initialize step from URL once, then maintain in state
    const [currentStep, setCurrentStep] = useState(() => {
        const urlStep = searchParams.get('wizardStep');
        const schemaKey = searchParams.get('schemaKey');
        if (urlStep) {
            const step = parseInt(urlStep);
            // Cap at step 2 (we removed step 3)
            return step > 2 ? 2 : step;
        }
        // If schemaKey is provided, start at step 2
        if (schemaKey) {
            return 2;
        }
        return isEditMode ? 2 : 1;
    });
    const schemaKeyFromUrl = searchParams.get('schemaKey');
    const [selectedStainKey, setSelectedStainKey] = useState(schemaKeyFromUrl || '');
    
    // Use useMemo for derived form data
    const initialFormData = useMemo(() => {
        if (!initialItem) {
            return { name: '', description: '', filenamePattern: '', schemaFields: {} };
        }
        return {
            name: initialItem.name || '',
            description: initialItem.description || '',
            filenamePattern: initialItem.filenamePattern || '',
            schemaFields: initialItem.details || {},
        };
    }, [initialItem?._id, initialItem?.name, initialItem?.description, initialItem?.filenamePattern, initialItem?.details]);
    
    const [formData, setFormData] = useState(initialFormData);
    const [error, setError] = useState(null);
    const [fieldErrors, setFieldErrors] = useState({});

    // Fetch published stain schemas
    const { data: schemasResponse, isLoading: schemasLoading } = useQuery({
        queryKey: ['bdsa-schema', 'stain'],
        queryFn: () => api.get('/bdsa/schema', { query: { name: 'stain', includeDrafts: false } }),
        staleTime: 5 * 60 * 1000,
    });

    // Fetch existing stain definitions
    const { data: stainsResponse, isLoading: stainsLoading } = useQuery({
        queryKey: ['bdsa-stain'],
        queryFn: () => api.get('/bdsa/stain'),
        staleTime: 5 * 60 * 1000,
    });

    // Automatically select latest schema
    const latestSchema = useMemo(() => {
        if (schemasResponse?.status === 200 && Array.isArray(schemasResponse.data) && schemasResponse.data.length > 0) {
            return schemasResponse.data.sort((a, b) => (b.version || 0) - (a.version || 0))[0];
        }
        return null;
    }, [schemasResponse]);

    const createStainMutation = useMutation({
        mutationFn: async (stainData) => {
            if (isEditMode) {
                const response = await api.put(`/bdsa/stain/${initialItem._id}`, {
                    body: stainData.body,
                });
                if (response.status !== 200) {
                    const errorMessage = response.data?.message || 
                                       response.data?.error || 
                                       (typeof response.data === 'string' ? response.data : null) ||
                                       response.data?.statusText ||
                                       `Failed to update stain definition (${response.status})`;
                    const error = new Error(errorMessage);
                    error.response = response;
                    throw error;
                }
                return response;
            } else {
                const response = await api.post('/bdsa/stain', stainData);
                if (response.status !== 200) {
                    const errorMessage = response.data?.message || 
                                       response.data?.error || 
                                       (typeof response.data === 'string' ? response.data : null) ||
                                       response.data?.statusText ||
                                       `Failed to create stain definition (${response.status})`;
                    const error = new Error(errorMessage);
                    error.response = response;
                    throw error;
                }
                return response;
            }
        },
        onSuccess: () => {
            queryClient.invalidateQueries(['bdsa-stain']);
            if (onComplete) {
                onComplete();
            }
        },
        onError: (err) => {
            const errorMessage = err.message || 
                               err.response?.data?.message ||
                               err.response?.data?.error ||
                               (isEditMode ? 'Failed to update stain definition' : 'Failed to create stain definition');
            setError(errorMessage);
        },
    });

    const deleteStainMutation = useMutation({
        mutationFn: async () => {
            const response = await api.delete(`/bdsa/stain/${initialItem._id}`);
            if (response.status !== 200) {
                const errorMessage = response.data?.message || 
                                   response.data?.error || 
                                   (typeof response.data === 'string' ? response.data : null) ||
                                   response.data?.statusText ||
                                   `Failed to delete stain definition (${response.status})`;
                const error = new Error(errorMessage);
                error.response = response;
                throw error;
            }
            return response;
        },
        onSuccess: () => {
            queryClient.invalidateQueries(['bdsa-stain']);
            if (onComplete) {
                onComplete();
            }
        },
        onError: (err) => {
            const errorMessage = err.message || 
                               err.response?.data?.message ||
                               err.response?.data?.error ||
                               'Failed to delete stain definition';
            setError(errorMessage);
        },
    });

    // Get available stains from latest schema
    const availableStains = useMemo(() => {
        if (!latestSchema) return [];
        const stainProperties = latestSchema.definition?.properties?.bdsa_stain_id?.items?.properties || {};
        return Object.entries(stainProperties).map(([key, value]) => ({
            key,
            title: value.title || key,
            abbreviation: value.abbreviation || '',
            stainType: value.stainType || '',
        }));
    }, [latestSchema]);

    // Extract schema properties for selected stain
    const stainSchemaProperties = useMemo(() => {
        if (!selectedStainKey || !latestSchema) return {};
        const stainDef = latestSchema.definition?.properties?.bdsa_stain_id?.items?.properties[selectedStainKey];
        return stainDef?.properties || {};
    }, [selectedStainKey, latestSchema]);

    // Map schema stain keys to existing stain definitions
    const existingStainKeys = useMemo(() => {
        if (stainsResponse?.status !== 200 || !Array.isArray(stainsResponse.data)) {
            return new Set();
        }
        const keys = new Set();
        stainsResponse.data.forEach(stain => {
            // Use schemaStainKey exclusively - no fallback to name matching
            if (stain.schemaStainKey) {
                keys.add(stain.schemaStainKey);
            }
        });
        return keys;
    }, [stainsResponse]);

    // Initialize form data from initialItem if editing, or from schemaKey if provided
    useEffect(() => {
        if (initialItem && latestSchema) {
            setFormData({
                name: initialItem.name || '',
                description: initialItem.description || '',
                schemaFields: initialItem.details || {},
            });
            // Use schemaStainKey directly from initialItem - no extraction from details or name matching
            if (initialItem.schemaStainKey) {
                setSelectedStainKey(initialItem.schemaStainKey);
            }
        } else if (schemaKeyFromUrl && latestSchema && !isEditMode) {
            // If schemaKey is provided in URL, set it
            setSelectedStainKey(schemaKeyFromUrl);
        }
    }, [initialItem?._id, initialItem?.schemaStainKey, latestSchema, schemaKeyFromUrl, isEditMode]);

    // Auto-fill form when stain is selected (only in create mode)
    useEffect(() => {
        if (!isEditMode && selectedStainKey && latestSchema) {
            const stain = availableStains.find(s => s.key === selectedStainKey);
            if (stain) {
                const isOtherCategory = selectedStainKey === 'Other Histochemical' || selectedStainKey === 'Other IHC';
                const stainDef = latestSchema.definition?.properties?.bdsa_stain_id?.items?.properties[selectedStainKey];
                const defaultFields = {};
                
                // Set default values for all properties that have single enum values
                if (stainDef?.properties) {
                    Object.entries(stainDef.properties).forEach(([key, prop]) => {
                        if (prop.enum && prop.enum.length === 1) {
                            // Fixed value - set as default
                            defaultFields[key] = prop.enum[0];
                        }
                    });
                }
                
                setFormData(prev => ({
                    ...prev,
                    name: isOtherCategory ? '' : stain.title, // Don't auto-fill name for "Other" categories
                    description: '',
                    schemaFields: {
                        ...prev.schemaFields,
                        ...defaultFields,
                    },
                }));
            }
        }
    }, [selectedStainKey, latestSchema, availableStains, isEditMode]);

    // Stable navigation function - must be before any early returns
    const goToStep = useCallback((step) => {
        setCurrentStep(step); // Update local state immediately
        const params = new URLSearchParams(searchParams);
        params.set('wizardStep', step.toString());
        // Preserve wizard, editId, and schemaKey if they exist
        if (searchParams.get('wizard')) {
            params.set('wizard', searchParams.get('wizard'));
        }
        if (searchParams.get('editId')) {
            params.set('editId', searchParams.get('editId'));
        }
        if (searchParams.get('schemaKey')) {
            params.set('schemaKey', searchParams.get('schemaKey'));
        }
        navigate(`/manage/stain?${params.toString()}`);
    }, [searchParams, navigate]);

    // Use ref for goToStep to avoid dependency issues in onClick handlers - must be before any early returns
    const goToStepRef = useRef(goToStep);
    goToStepRef.current = goToStep;

    // Ensure we're on step 2 in edit mode (safety check) - must be after goToStepRef is set up
    useEffect(() => {
        if (isEditMode && currentStep === 1) {
            goToStepRef.current(2);
        }
    }, [isEditMode, currentStep]);

    // Sync with URL on mount/URL change (but only if different) - must be before any early returns
    const urlStepValue = searchParams.get('wizardStep');
    useEffect(() => {
        if (urlStepValue) {
            const stepNum = parseInt(urlStepValue);
            // In edit mode, never allow step 1
            if (isEditMode && stepNum === 1) {
                // Force step 2 in edit mode
                setCurrentStep(2);
                const params = new URLSearchParams(searchParams);
                params.set('wizardStep', '2');
                navigate(`/manage/stain?${params.toString()}`, { replace: true });
            } else if (stepNum !== currentStep && stepNum >= 1 && stepNum <= 2) {
                setCurrentStep(stepNum);
            }
        } else if (isEditMode && currentStep === 1) {
            // If no URL step and we're in edit mode at step 1, go to step 2
            setCurrentStep(2);
            const params = new URLSearchParams(searchParams);
            params.set('wizardStep', '2');
            if (searchParams.get('wizard')) {
                params.set('wizard', searchParams.get('wizard'));
            }
            if (searchParams.get('editId')) {
                params.set('editId', searchParams.get('editId'));
            }
            navigate(`/manage/stain?${params.toString()}`, { replace: true });
        }
    }, [urlStepValue, currentStep, isEditMode, searchParams, navigate]);

    // Show error if no schema
    if (!schemasLoading && !latestSchema) {
        return (
            <SetupPageLayout
                stepNumber={1}
                totalSteps={1}
                title="Create Stain Definition"
                description="Create a new stain definition based on a published schema"
                onPrevious={onCancel}
                onNext={onCancel}
                nextLabel="Back"
            >
                <div className={styles.error}>No published stain schema found. Please publish a schema first.</div>
            </SetupPageLayout>
        );
    }

    const handleNext = () => {
        if (currentStep === 1) {
            if (!selectedStainKey) {
                setError('Please select a stain from the schema');
                return;
            }
            goToStep(2);
            setError(null);
        } else if (currentStep === 2) {
            if (validateAllFields()) {
                handleCreate();
            } else {
                setError('Please fix the errors below');
            }
        }
    };

    const handleBack = () => {
        if (currentStep > 1) {
            goToStep(currentStep - 1);
            setError(null);
        } else {
            // Go back to parent (close wizard)
            onCancel();
        }
    };

    const handleCreate = () => {
        if (!validateAllFields()) {
            setError('Please fix the errors below');
            return;
        }

        if (!selectedStainKey) {
            setError('Please select a stain from the schema');
            return;
        }

        const stainData = {
            query: {
                name: formData.name,
            },
            body: {
                schema_id: initialItem?.schemaId || latestSchema?._id,
                schema_stain_key: selectedStainKey,
                details: {
                    ...formData.schemaFields,
                    // Remove schema key markers - details should only contain schema-specific metadata
                },
                description: formData.description || '',
                used_from: null, // No longer used
                used_to: null, // No longer used
                filename_pattern: (formData.filenamePattern || '').trim() || '',
            },
        };

        createStainMutation.mutate(stainData);
    };

    const handleDelete = () => {
        if (window.confirm(`Are you sure you want to delete "${formData.name || initialItem?.name}"? This action cannot be undone.`)) {
            deleteStainMutation.mutate();
        }
    };

    const renderSchemaField = (key, prop) => {
        const stainDef = latestSchema.definition?.properties?.bdsa_stain_id?.items?.properties[selectedStainKey];
        
        // Check if this field has a fixed enum value (only one option)
        const hasFixedEnum = prop.enum && prop.enum.length === 1;
        const fixedValue = hasFixedEnum ? prop.enum[0] : null;
        
        // Use form data value, or fixed value if field is empty and has fixed enum
        const currentValue = formData.schemaFields[key];
        const value = currentValue || fixedValue || '';
        
        const isRequired = latestSchema.definition?.properties?.bdsa_stain_id?.items?.properties[selectedStainKey]?.required?.includes(key);
        const fieldError = fieldErrors[`schemaField_${key}`];
        const isValid = !fieldError && (isRequired ? value : true);
        
        if (prop.enum) {
            // Render as dropdown
            return (
                <div key={key} className={styles.formField}>
                    <label className={isRequired ? styles.requiredLabel : ''}>
                        {prop.title || key} {isRequired && '*'}
                    </label>
                    <div className={styles.inputWrapper}>
                        <select
                            value={value}
                            onChange={(e) => {
                                if (!hasFixedEnum) {
                                    setFormData(prev => ({
                                        ...prev,
                                        schemaFields: { ...prev.schemaFields, [key]: e.target.value }
                                    }));
                                    validateField(`schemaField_${key}`, e.target.value);
                                }
                            }}
                            onBlur={() => validateField(`schemaField_${key}`, value)}
                            disabled={hasFixedEnum}
                            className={fieldError ? styles.fieldError : (isValid && isRequired ? styles.fieldValid : '')}
                        >
                            {!hasFixedEnum && <option value="">-- Select --</option>}
                            {prop.enum.map(opt => (
                                <option key={opt} value={opt}>{opt}</option>
                            ))}
                        </select>
                        {isValid && isRequired && <span className={styles.fieldValidIcon}>✓</span>}
                    </div>
                    {hasFixedEnum && key === 'phospho-specific' && (
                        <small style={{fontStyle: 'italic', color: '#6a737d'}}>
                            This stain is {fixedValue === 'yes' ? 'phospho-specific' : 'not phospho-specific'} by default
                        </small>
                    )}
                    {fieldError && <span className={styles.fieldErrorMessage}>{fieldError}</span>}
                    {prop.description && <small>{prop.description}</small>}
                </div>
            );
        } else if (prop.type === 'string') {
            // Render as text input
            return (
                <div key={key} className={styles.formField}>
                    <label className={isRequired ? styles.requiredLabel : ''}>
                        {prop.title || key} {isRequired && '*'}
                    </label>
                    <div className={styles.inputWrapper}>
                        <input
                            type="text"
                            value={value}
                            onChange={(e) => {
                                setFormData(prev => ({
                                    ...prev,
                                    schemaFields: { ...prev.schemaFields, [key]: e.target.value }
                                }));
                                if (fieldError) validateField(`schemaField_${key}`, e.target.value);
                            }}
                            onBlur={() => validateField(`schemaField_${key}`, value)}
                            placeholder={prop.pattern ? prop.examples?.[0] : ''}
                            pattern={prop.pattern}
                            className={fieldError ? styles.fieldError : (isValid && isRequired ? styles.fieldValid : '')}
                        />
                        {isValid && isRequired && <span className={styles.fieldValidIcon}>✓</span>}
                    </div>
                    {fieldError && <span className={styles.fieldErrorMessage}>{fieldError}</span>}
                    {prop.pattern && prop.examples && (
                        <small>Format: {prop.examples.join(', ')}</small>
                    )}
                    {prop.description && <small>{prop.description}</small>}
                </div>
            );
        }
        return null;
    };

    // Validation functions
    const validateField = (fieldName, value) => {
        const errors = { ...fieldErrors };
        if (fieldName === 'name') {
            if (!value || !value.trim()) {
                errors.name = 'Name is required';
            } else {
                delete errors.name;
            }
        } else if (fieldName.startsWith('schemaField_')) {
            const schemaKey = fieldName.replace('schemaField_', '');
            const required = latestSchema.definition?.properties?.bdsa_stain_id?.items?.properties[selectedStainKey]?.required || [];
            if (required.includes(schemaKey) && (!value || (Array.isArray(value) && value.length === 0))) {
                errors[fieldName] = 'This field is required';
            } else {
                delete errors[fieldName];
            }
        }
        setFieldErrors(errors);
        return !errors[fieldName];
    };

    const validateAllFields = () => {
        const errors = {};
        if (!formData.name || !formData.name.trim()) {
            errors.name = 'Name is required';
        }
        if (selectedStainKey && latestSchema) {
            const required = latestSchema.definition?.properties?.bdsa_stain_id?.items?.properties[selectedStainKey]?.required || [];
            required.forEach(key => {
                const value = formData.schemaFields[key];
                if (!value || (Array.isArray(value) && value.length === 0)) {
                    errors[`schemaField_${key}`] = 'This field is required';
                }
            });
        }
        setFieldErrors(errors);
        return Object.keys(errors).length === 0;
    };

    const canProceed = () => {
        if (isEditMode) {
            if (currentStep === 2) {
                if (!formData.name.trim()) return false;
                const required = latestSchema.definition?.properties?.bdsa_stain_id?.items?.properties[selectedStainKey]?.required || [];
                return required.every(key => formData.schemaFields[key]);
            }
            return true;
        }
        if (currentStep === 1) return selectedStainKey !== '';
        if (currentStep === 2) {
            if (!formData.name.trim()) return false;
            const required = latestSchema.definition?.properties?.bdsa_stain_id?.items?.properties[selectedStainKey]?.required || [];
            return required.every(key => formData.schemaFields[key]);
        }
        return true;
    };

    const renderStep = () => {
        switch (currentStep) {
            case 1:
                if (isEditMode) {
                    // Skip step 1 in edit mode, go directly to step 2
                    return null;
                }
                return (
                    <div className={styles.stepContent}>
                        <h3>Select Stain</h3>
                        <p>Choose a stain from the schema. The name will be auto-filled.</p>
                        <div style={{fontSize: '13px', color: '#6a737d', marginTop: '0.5em', padding: '0.75em', background: '#f6f8fa', borderRadius: '6px', border: '1px solid #e1e4e8'}}>
                            <strong>About stain definitions:</strong> Stain definitions are based on entries in the stain schema. 
                            The short name comes from the schema and cannot be changed. 
                            Definitions add local details like vendor, dilution, or antibody information. 
                            To add new stain types to the schema, use the schema editor.
                        </div>
                        {availableStains.length === 0 ? (
                            <div className={styles.error}>No stains found in the selected schema.</div>
                        ) : (
                            <div className={styles.stainList}>
                                {availableStains.map(stain => (
                                    <div
                                        key={stain.key}
                                        className={`${styles.stainCard} ${selectedStainKey === stain.key ? styles.selected : ''} ${existingStainKeys.has(stain.key) ? styles.hasDefinition : ''}`}
                                        onClick={() => {
                                            setSelectedStainKey(stain.key);
                                            setError(null);
                                            goToStep(2);
                                        }}
                                    >
                                        <div className={styles.stainCardHeader}>
                                            <strong>{stain.title}</strong>
                                            {existingStainKeys.has(stain.key) && (
                                                <span className={styles.definedBadge} title="Stain definition already exists">
                                                    ✓ Defined
                                                </span>
                                            )}
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                );

            case 2:
                const selectedStain = availableStains.find(s => s.key === selectedStainKey);
                const schemaStainName = selectedStain?.title || '';
                const schemaStainAbbr = selectedStain?.abbreviation || '';
                const schemaStainType = selectedStain?.stainType || '';
                const isOtherCategory = selectedStainKey === 'Other Histochemical' || selectedStainKey === 'Other IHC';
                
                return (
                    <div className={styles.stepContent}>
                        <h3>Configure Details</h3>
                        <p>Review and edit the stain definition details.</p>
                        <div className={styles.form}>
                            {/* Schema Info (Read-only) */}
                            <div className={styles.schemaInfo}>
                                <span className={styles.schemaInfoLabel}>Schema:</span>
                                <span className={styles.schemaInfoName}>{schemaStainName}</span>
                                {schemaStainAbbr && (
                                    <>
                                        <span className={styles.schemaInfoSeparator}>|</span>
                                        <span className={styles.schemaInfoAbbr}>{schemaStainAbbr}</span>
                                    </>
                                )}
                                {schemaStainType && (
                                    <>
                                        <span className={styles.schemaInfoSeparator}>|</span>
                                        <span className={styles.schemaInfoType}>{schemaStainType}</span>
                                    </>
                                )}
                            </div>

                            <div className={styles.formField}>
                                <label className={styles.requiredLabel}>
                                    {isOtherCategory ? 'Stain Name *' : 'Display Name *'}
                                </label>
                                <div className={styles.inputWrapper}>
                                    <input
                                        type="text"
                                        value={formData.name}
                                        onChange={(e) => {
                                            setFormData({ ...formData, name: e.target.value });
                                            if (fieldErrors.name) validateField('name', e.target.value);
                                        }}
                                        onBlur={() => validateField('name', formData.name)}
                                        placeholder={isOtherCategory ? "Enter custom stain name" : "e.g., H&E, IHC"}
                                        className={fieldErrors.name ? styles.fieldError : (formData.name ? styles.fieldValid : '')}
                                    />
                                    {!fieldErrors.name && formData.name && <span className={styles.fieldValidIcon}>✓</span>}
                                </div>
                                {fieldErrors.name && <span className={styles.fieldErrorMessage}>{fieldErrors.name}</span>}
                                <small>
                                    {isOtherCategory 
                                        ? 'Enter the name for this custom stain definition' 
                                        : 'Display name for this stain definition'}
                                </small>
                            </div>

                            <div className={styles.formField}>
                                <label>Description</label>
                                <textarea
                                    value={formData.description}
                                    onChange={(e) => {
                                        setFormData({ ...formData, description: e.target.value });
                                    }}
                                    placeholder="Optional description displayed to users when choosing stains"
                                    rows={3}
                                    className={styles.textarea}
                                />
                                <small>Optional description for this stain definition</small>
                            </div>

                            {/* Stain Configuration - Enum dropdowns, dilution, and vendor */}
                            {Object.entries(stainSchemaProperties).some(([key, prop]) => prop.enum || key === 'dilution' || key === 'vendor') && (
                                <div className={styles.formSection}>
                                    <div className={styles.sectionHeader}>
                                        <div>
                                            <h4>Stain Configuration</h4>
                                            <small style={{display: 'block', marginTop: '0.25em'}}>Select stain-specific configuration options</small>
                                        </div>
                                    </div>
                                    <div className={styles.sectionContent}>
                                        {Object.entries(stainSchemaProperties)
                                            .filter(([key, prop]) => prop.enum || key === 'dilution' || key === 'vendor')
                                            .map(([key, prop]) => renderSchemaField(key, prop))
                                        }
                                    </div>
                                </div>
                            )}

                            {/* Additional Properties - Text inputs */}
                            {Object.entries(stainSchemaProperties)
                                .filter(([key, prop]) => !prop.enum && key !== 'dilution' && key !== 'vendor')
                                .map(([key, prop]) => renderSchemaField(key, prop))
                            }

                            <FilenamePatternField
                                value={formData.filenamePattern}
                                onChange={(next) => setFormData({ ...formData, filenamePattern: next })}
                                variant="section"
                            />
                        </div>
                    </div>
                );

            default:
                return null;
        }
    };

    const selectedStainName = availableStains.find(s => s.key === selectedStainKey)?.title || '';

    const nextLabelValue = currentStep === 2 
        ? (createStainMutation.isPending 
            ? 'Saving...' 
            : 'Save and Return')
        : (selectedStainName ? `Continue with ${selectedStainName}` : 'Continue');

    return (
        <SetupPageLayout
            stepNumber={currentStep}
            totalSteps={2}
            title={isEditMode ? "Edit Stain Definition" : "Create Stain Definition"}
            description={isEditMode ? "Edit the stain definition" : "Create a new stain definition based on the latest published stain schema"}
            showHeaderNavigation={true}
            backLink="/manage/stain"
            backLinkText="Stain Setup"
            onPrevious={currentStep > 1 ? handleBack : onCancel}
            onNext={handleNext}
            nextDisabled={!canProceed() || createStainMutation.isPending}
            nextLabel={nextLabelValue}
            previousLabel={currentStep > 1 ? 'Back' : 'Cancel'}
        >
            {error && <div className={styles.error}>{error}</div>}
            {renderStep()}
            {currentStep === 2 && (
                <WizardFooter
                    onPrevious={currentStep > 1 ? handleBack : null}
                    onNext={handleNext}
                    nextDisabled={!canProceed() || createStainMutation.isPending}
                    nextLabel={createStainMutation.isPending 
                        ? 'Saving...' 
                        : 'Save and Return'}
                    previousLabel={currentStep > 1 ? 'Back' : 'Cancel'}
                    showPrevious={currentStep > 1}
                    isPrimaryAction={true}
                    onDelete={isEditMode ? handleDelete : null}
                    deleteDisabled={isEditMode && deleteStainMutation.isPending}
                    deleteLabel={isEditMode && deleteStainMutation.isPending ? 'Deleting...' : 'Delete'}
                />
            )}
        </SetupPageLayout>
    );
}

export default StainDefinitionWizard;
